import {
    collection,
    component,
    EntityData,
    FedeltaImageData,
    field,
    ImageUtils,
    localDateField,
    SearchModel,
    SearchResult,
    SearchSortField,
    timeField,
    transientField
} from "@fedelta-pos/data-client";
import {Inject, Injectable, InjectionToken, Injector, Optional} from "@angular/core";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {FilterProvider} from "../config-support/filter.provider";
import {IntegratedPaymentProvider} from "../payment/payment";
import {OrderType} from "../order/order";

export const STORE_SEARCH_FILTER_PROVIDER: InjectionToken<FilterProvider> = new InjectionToken<FilterProvider>("StoreSearchFilterProvider");

export enum StoreDisplayStatus {
    OFFLINE,
    CURRENTLY_CLOSED,
    CLOSED_AT_TIME,
    CURRENTLY_OPEN,
    OPEN_AT_TIME
}

// noinspection JSUnusedGlobalSymbols
export enum StoreOpeningHoursSpecifier {
    DAY_OF_WEEK = <any>"DAY_OF_WEEK",
    DATE = <any>"DATE",
    DATE_SCHEDULE = <any>"DATE_SCHEDULE",
    TIME_SCHEDULE = <any>"TIME_SCHEDULE"
}

export class HoursData extends EntityData<HoursData> {
    @timeField("hh:mm a") start: Date;
    @timeField("hh:mm a") end: Date;
}

export class DayHoursData extends EntityData<HoursData> {
    @collection(HoursData) hours: HoursData[];
    @field closedForTheDay: boolean;
}

export class NonStandardHours extends EntityData<NonStandardHours> {
    @field specifier: StoreOpeningHoursSpecifier;
    @localDateField specificLocalDate: Date;
    @field schedule: string;
    @component hours: DayHoursData;
}

export class OpeningHoursData extends EntityData<OpeningHoursData> {
    @component monday: DayHoursData;
    @component tuesday: DayHoursData;
    @component wednesday: DayHoursData;
    @component thursday: DayHoursData;
    @component friday: DayHoursData;
    @component saturday: DayHoursData;
    @component sunday: DayHoursData;

    @collection(NonStandardHours) nonStandardHours: NonStandardHours[];
}

export class TableCodeBarcodeData extends EntityData<TableCodeBarcodeData> {
    @field barcode: string;
    @field tableCode: string;
}

export class StoreData extends EntityData<StoreData> {
    @field webTerminalId: number;
    @field venueId: string;
    @field paymentProvider: IntegratedPaymentProvider;
    @field webTerminalName: string;
    @field name: string;
    @field storeGroup: string;
    @field description: string;
    @field menuId: number;
    @field orderAtTableStore: boolean;
    @field orderAheadStore: boolean;
    @field orderAheadLaterTimeStore: boolean = true;
    @field orderAheadLaterStore: boolean;
    @field orderDelivery: boolean;
    @field completionTime: number;
    @field currentWaitTimeMinutes: number;
    @field maxDaysOrderLater: number;
    @field tableCode: string;
    @field city: string;
    @field postcode: string;
    @field state: string;
    @field street: string;
    @field address: string;
    @field telephone: string;
    @field outletId: number;
    @field outletName: string;
    @field outletDescription: string;
    @field locationId: number;
    @field locationName: string;
    @field latitude: number;
    @field longitude: number;
    @field timeZone: string;
    @field orderLocalDateTime: string; // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss
    @field startLocalDateTime: string; // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss - corresponds to system start time
    @field endLocalDateTime: string;   // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss - corresponds to system end time
    @field available: boolean;
    @field closed: boolean;
    @field closedAtOrderDateTime: boolean;
    @field offline: boolean;
    @field minPickupOrderAmount: number;
    @field maxPickupOrderAmount: number;
    @field minDeliveryOrderAmount: number;
    @field maxDeliveryOrderAmount: number;
    @field minTableOrderAmount: number;
    @field maxTableOrderAmount: number;
    @field allowTableGroupOrders: boolean = true;
    @field allowPickupGroupOrders: boolean = false;
    @field sendTableOrderReady: boolean = false;
    @field allowTips: boolean = false;
    @field highlightTips: boolean = false;
    @field tipInstructions: string;
    @field tipPercentage1: number;
    @field tipPercentage2: number;
    @field tipPercentage3: number;
    @field tipPercentage4: number;
    @component storeOpeningHours: OpeningHoursData;
    @component image: FedeltaImageData;
    @field imageOpacity: number;
    @component locationImage: FedeltaImageData;
    @field horizontalLogoImage: string;
    @field horizontalLogoHeightPx: number;
    @field verticalLogoImage: string;
    @field verticalLogoWidthPx: number;
    @field integratedDelivery: boolean;
    @field externalAppURL: string;
    @field externalAppWebTerminalId: number;
    @field parentAppStore: boolean;
    @field minimumAge: number = 18;  // defaulted for pre-7.18 backend
    @collection(TableCodeBarcodeData) tableCodeBarcodes: TableCodeBarcodeData[];

    /**
     * Calculated distance from a specified location
     */
    @transientField distance: number;

    @transientField _horizontalLogoImageSvg: string;
    @transientField _verticalLogoImageSvg: string;

    get tableNumber(): string {
        return this.tableCode;
    }

    set tableNumber(tableNumber: string) {
        this.tableCode = tableNumber;
    }

    get horizontalLogoImageSvg(): string {
        if (this._horizontalLogoImageSvg === undefined) {
            this._horizontalLogoImageSvg = ImageUtils.base64ToSvg(this.horizontalLogoImage) || null;
        }
        return this._horizontalLogoImageSvg || undefined;
    }

    get verticalLogoImageSvg(): string {
        if (this._verticalLogoImageSvg === undefined) {
            this._verticalLogoImageSvg = ImageUtils.base64ToSvg(this.verticalLogoImage) || null;
        }
        return this._verticalLogoImageSvg || undefined;
    }

    canOrder(orderType: OrderType): boolean {
        return (orderType === OrderType.ORDER_AHEAD && this.orderAheadStore) ||
            (orderType === OrderType.ORDER_DELIVERY && this.orderDelivery) ||
            (orderType === OrderType.ORDER_AT_TABLE && this.orderAtTableStore);
    }

    getDisplayStatus(orderType: OrderType): StoreDisplayStatus {
        return orderType === OrderType.ORDER_AT_TABLE ? (this.closed ? StoreDisplayStatus.CURRENTLY_CLOSED : this.offline ? StoreDisplayStatus.OFFLINE : StoreDisplayStatus.CURRENTLY_OPEN)
            : this.closedAtOrderDateTime ? (this.orderLocalDateTime ? StoreDisplayStatus.CLOSED_AT_TIME : StoreDisplayStatus.CURRENTLY_CLOSED)
                : this.offline ? StoreDisplayStatus.OFFLINE
                    : (this.orderLocalDateTime ? StoreDisplayStatus.OPEN_AT_TIME : StoreDisplayStatus.CURRENTLY_OPEN);
    }
}

@Injectable()
export class StoreSearchModel extends SearchModel<StoreData> {
    public static readonly FILTER_LOOKUP_CODE = "barcode";
    public static readonly FILTER_ORDER_LOCAL_DATE_TIME = "orderLocalDateTime";
    public static readonly FILTER_SINGLE_STORE_ID = "singleStoreId";
    public static readonly FILTER_STORE_GROUP = "storeGroup";
    public static readonly FILTER_STORES = "stores";
    public static readonly FILTER_ORDER_AHEAD_ONLY = "orderAheadOnly";
    public static readonly FILTER_ORDER_DELIVERY_ONLY = "orderDeliveryOnly";
    public static readonly FILTER_ORDER_AT_TABLE_ONLY = "orderAtTableOnly";
    public static readonly FILTER_DISABLE_OTHER_ORDER_TYPES = "disableOtherOrderTypes";
    public static readonly FILTER_LOCAL_APP_URL = "localAppURL";
    public static readonly FILTER_PARENT_APP_URL = "parentAppURL";

    public hasOrderAheadStores$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public hasOrderDeliveryStores$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public hasOrderAtTableStores$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public hasStores$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public hasMultipleStoreTypes$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public nearestStores: Array<StoreData> = [];
    public nearestStores$: Subject<StoreData[]> = new Subject();

    constructor(injector: Injector,
                @Optional() @Inject(STORE_SEARCH_FILTER_PROVIDER) private filterProvider: FilterProvider) {
        super(injector, StoreData);
        console.log("--- StoreSearchModel");

        this.sort = [new SearchSortField("state", "asc"), new SearchSortField("name", "asc")];
    }

    public determineAvailableStores(): void {
        const hasOrderAheadStores: boolean = this.data.findIndex((store: StoreData) => store.orderAheadStore) >= 0;
        const hasOrderDeliveryStores: boolean = this.data.findIndex((store: StoreData) => store.orderDelivery) >= 0;
        const hasOrderAtTableStores: boolean = this.data.findIndex((store: StoreData) => store.orderAtTableStore) >= 0;
        this.hasOrderAheadStores$.next(hasOrderAheadStores);
        this.hasOrderDeliveryStores$.next(hasOrderDeliveryStores);
        this.hasOrderAtTableStores$.next(hasOrderAtTableStores);
        this.hasStores$.next(hasOrderAheadStores || hasOrderDeliveryStores || hasOrderAtTableStores);
        this.hasMultipleStoreTypes$.next(((hasOrderAheadStores ? 1 : 0) + (hasOrderDeliveryStores ? 1 : 0) + (hasOrderAtTableStores ? 1 : 0)) > 1);
    }

    /**
     * Finds the nearest stores from specified coordinates. The search should be called before calling this method
     * Stores are ordered by distance.
     * Only stores within StoreSearchModel.MAX_DISTANCE are included.
     *
     * @param storeLimit
     * @param storeRange
     * @param deliveryStoreRange
     * @param latitude
     * @param longitude
     */
    public async loadNearestStores(storeLimit: number, storeRange: number, deliveryStoreRange: number, latitude?: number, longitude?: number): Promise<any> {
        this.nearestStores = [];
        return new Promise((resolve) => {
            if (latitude && longitude) {
                this.findNearestStores(storeLimit, storeRange, deliveryStoreRange, latitude, longitude);
            }
            resolve();
        });
    }

    /**
     * Finds the nearest stores from specified coordinates. The search should be called before calling this method
     * Stores are ordered by distance.
     * Only stores within StoreSearchModel.MAX_DISTANCE are included.
     *
     * If the stores have not yet been loaded, an empty array will be returned.
     *
     * @param storeLimit
     * @param storeRange
     * @param deliveryStoreRange
     * @param latitude
     * @param longitude
     * @param deliveryOnly
     */
    public findNearestStores(storeLimit: number, storeRange: number, deliveryStoreRange: number, latitude: number, longitude: number, deliveryOnly: boolean = false): void {
        this.nearestStores = StoreSearchModel.applyNearestStoreLimit(storeLimit, this.data
            .filter((store: StoreData) => store.latitude && store.longitude)
            .filter((store: StoreData) => !deliveryOnly || store.orderDelivery)
            .map((store: StoreData) => {
                store.distance = this.distance(latitude, longitude, store.latitude, store.longitude, "K", 2);
                return store;
            })
            .filter((store: StoreData) => {
                if (deliveryStoreRange && store.orderDelivery && (deliveryOnly || (!store.orderAheadStore && !store.orderAtTableStore))) {
                    return store.distance < deliveryStoreRange;
                } else if (storeRange && !deliveryOnly) {
                    return store.distance < storeRange;
                }
                return true;
            }));
        this.nearestStores$.next(this.nearestStores);
    }

    public static applyNearestStoreLimit(storeLimit: number, stores: StoreData[]): StoreData[] {
        stores.sort((a: StoreData, b: StoreData) => a.distance - b.distance);
        let locationIds: number[] = stores.map((store: StoreData) => store.locationId);
        locationIds = locationIds.filter((locationId: number, pos: number) => locationIds.indexOf(locationId) === pos).slice(0, storeLimit);
        return stores.filter((store: StoreData) => locationIds.indexOf(store.locationId) >= 0);
    }

    public clearNearestStores(): void {
        this.nearestStores = [];
    }

    /**
     * Calculate the distance between two points
     * @param lat1
     * @param lon1
     * @param lat2
     * @param lon2
     * @param unit
     * @param sigDigits
     * @returns {number}
     */
    private distance(lat1: number, lon1: number, lat2: number, lon2: number, unit: string, sigDigits: number): number {
        let radlat1: number = Math.PI * lat1 / 180;
        let radlat2: number = Math.PI * lat2 / 180;
        let theta: number = lon1 - lon2;
        let radtheta: number = Math.PI * theta / 180;
        let dist: number = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI;
        dist = dist * 60 * 1.1515;
        if (unit === "K") {
            dist = dist * 1.609344;
        }
        if (unit === "M") {
            dist = dist * 0.8684;
        }
        let multiplier: number = Math.pow(10, sigDigits - Math.floor(Math.log(dist) / Math.LN10) - 1);
        return Math.round(dist * multiplier) / multiplier;
    }

    searchRecords(filter: any, start: number, limit: number, sort: SearchSortField[]): Observable<SearchResult> {
        let currentFilter: any = Object.assign({}, filter);
        if (this.filterProvider) {
            const interceptFilters: any = this.filterProvider.getFilters(currentFilter);
            if (interceptFilters) {
                for (let key in interceptFilters) {
                    currentFilter[key] = interceptFilters[key];
                }
            }
        }
        return super.searchRecords(currentFilter, start, limit, sort);
    }
}



// WEBPACK FOOTER //
// ./src/app/store/store-search.model.ts